import FilterListIcon from "@mui/icons-material/FilterList";
import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MenuListParams } from "../../../../api/services/client/models";
import { useGetSolutionOffering } from "../../../../api/services/programTemplate/programTemplateApis";
import {
  InformationBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiButtonTertiary,
  MuiGrid,
  MuiModal,
  MuiOptionMenu,
} from "../../../../components";
import { CommonListModal } from "../../../../components/commonListModal/CommonListModal";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";
import { STATUS_BADGE_TYPE } from "../../../../components/statusBadge/StatusBadge";
import { VersionTrackerModal } from "../../../../components/versionTracker/MuiVersionTracker";
import {
  LANGUAGE_CODE,
  MODULE_PERMISSIONS,
  PAGE_URLS,
} from "../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import {
  getPropertyFromArray,
  isEmpty,
  isNotEmpty,
  isSolutionOfferingAllowedForTransfer,
} from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { LabelWithIconText } from "../../../../styles/common.style";
import { InvoiceHistory } from "../../../createProgram/components/invoiceHistory/InvoiceHistoryMain";
import { programSiteCoreKeys } from "../../../createProgram/siteCore/SiteCoreKeys";
import { ViewProgramHeaderWrapper } from "./ViewProgramHeader.style";

interface ViewProgramHeaderProps {
  title: string;
  onEditHandler?: () => void;
  status?: string;
  solutionOffering?: string;
  onShowTransferHandler?: () => void;
  versionHistory?: MenuListParams[];
  isVersionLatest?: boolean;
  programId: string;
  isFromCreateProjectChildPrograms?: boolean;
  isEditable?: boolean;
}

export const ViewProgramHeader: FC<ViewProgramHeaderProps> = ({
  title,
  onEditHandler,
  status = "",
  solutionOffering = "",
  onShowTransferHandler,
  versionHistory,
  isVersionLatest,
  programId,
  isFromCreateProjectChildPrograms,
  isEditable,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { fetchLabel, commonLabels, fetchComponentTitle, fetchCommonLabel } =
    useSiteCore();
  const [showInvoiceHistoryModal, setShowInvoiceHistoryModal] = useState(false);
  const [showResponsiveVersionModal, setShowResponsiveVersionModal] =
    useState(false);
  const {
    data: listOfSolutionOfferings,
    isLoading: isOfferingPending,
    isError: isOfferingError,
  } = useGetSolutionOffering(LANGUAGE_CODE.EN_US, false, true);
  const { isMobile } = useMediaQueries();

  const actions = [
    {
      onClick: () => onShowTransferHandler?.(),
      dataTestId: "transfer-participant-btn",
      label: fetchComponentTitle(programSiteCoreKeys.transferParticipantTitle),
      ariaLabel: fetchComponentTitle(
        programSiteCoreKeys.transferParticipantTitle,
      ),
      disabled: !isVersionLatest,
    },
  ];

  const handleClose = () => {
    setShowResponsiveVersionModal(false);
  };

  const handleRowClick = (item: MenuListParams) => {
    handleClose();
    navigate(`${PAGE_URLS.VIEW_PROGRAM}/${item.id}`);
  };

  const getVersionName = () => {
    const path = location?.pathname;
    const selectedItem = versionHistory?.find(
      (item) => item.id && path?.includes(item.id),
    );
    return selectedItem?.name || fetchCommonLabel(commonSiteCoreKeys.version);
  };

  const versionItems = [
    {
      onClick: () => setShowResponsiveVersionModal(true),
      label: (
        <LabelWithIconText>
          <FilterListIcon />
          {getVersionName()}
        </LabelWithIconText>
      ),
      dataTestId: "version-history-list-button",
      disabled: isEmpty(versionHistory),
    },
  ];

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isOfferingPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isOfferingPending]);

  useEffect(() => {
    if (isOfferingError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isOfferingError]);

  const handleNavigate = () => {
    navigate(`${PAGE_URLS.EDIT_PROGRAM_TEMPLATE}/${id}`);
  };

  const showTransferButton = () => {
    if (
      isFromCreateProjectChildPrograms ||
      !solutionOffering ||
      isEmpty(listOfSolutionOfferings)
    )
      return false;
    return (
      status === STATUS_BADGE_TYPE.active &&
      isSolutionOfferingAllowedForTransfer(
        getPropertyFromArray(listOfSolutionOfferings, "name"),
        solutionOffering,
      )
    );
  };

  return (
    <ViewProgramHeaderWrapper>
      <MuiGrid container>
        <MuiGrid
          size={{ sm: 4, xs: 12 }}
          className="header-left"
          data-testid="view-program-template-header"
        >
          <InformationBox title={title} />
          {isMobile && <MuiOptionMenu menuItems={versionItems} />}
        </MuiGrid>

        <MuiGrid size={{ sm: 9, xs: 12 }} className="header-right">
          {!isMobile && (
            <MuiButtonTertiary
              className="version-history"
              disabled={isEmpty(versionHistory)}
            >
              <VersionTrackerModal
                history={versionHistory ?? []}
                isDisabledHistory={isEmpty(versionHistory)}
              />
            </MuiButtonTertiary>
          )}

          <PermissionWrapper
            permission={MODULE_PERMISSIONS.PROGRAM_VIEW_INVOICE_HISTORY}
          >
            <MuiButtonSecondary
              onClick={() => setShowInvoiceHistoryModal(true)}
              className="header-center-button"
            >
              {fetchLabel(
                programSiteCoreKeys.createProgramHeader,
                programSiteCoreKeys.viewInvoiceHistoryButton,
              )}
            </MuiButtonSecondary>
          </PermissionWrapper>

          {isEditable && (
            <MuiButtonPrimary
              onClick={onEditHandler ?? handleNavigate}
              className="header-right-button"
              id="edit-btn"
              disabled={!isVersionLatest}
            >
              {commonLabels.edit}
            </MuiButtonPrimary>
          )}

          {isEditable &&
            showTransferButton() &&
            (isMobile ? (
              <MuiButtonSecondary
                onClick={() => onShowTransferHandler?.()}
                className="header-right-button"
                data-testid="transfer-participant-btn"
                aria-label={fetchComponentTitle(
                  programSiteCoreKeys.transferParticipantTitle,
                )}
                disabled={!isVersionLatest}
              >
                {fetchComponentTitle(
                  programSiteCoreKeys.transferParticipantTitle,
                )}
              </MuiButtonSecondary>
            ) : (
              <MuiOptionMenu className="more-btn" menuItems={actions} />
            ))}
        </MuiGrid>
      </MuiGrid>

      {showInvoiceHistoryModal && (
        <MuiModal
          hasLargeContent
          open={showInvoiceHistoryModal}
          onClose={() => setShowInvoiceHistoryModal(false)}
          title={fetchCommonLabel(commonSiteCoreKeys.invoiceHistory)}
          primaryButtonTitle={commonLabels.ok}
          onPrimaryButtonClick={() => setShowInvoiceHistoryModal(false)}
        >
          <InvoiceHistory programId={programId} isViewOnly={true} />
        </MuiModal>
      )}

      {showResponsiveVersionModal && isNotEmpty(versionHistory) && (
        <CommonListModal
          title={fetchCommonLabel(commonSiteCoreKeys.version)}
          open={showResponsiveVersionModal}
          onFilterClick={handleRowClick}
          listItems={versionHistory}
          closeHandler={handleClose}
          secondaryButtonTitle={commonLabels.close}
        />
      )}
    </ViewProgramHeaderWrapper>
  );
};
